import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../utils/supabase";

const fmtMoney = (n) =>
  new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtDateInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const MONTH_OPTIONS = [
  { value: "", label: "Todos los meses" },
  { value: "01", label: "Enero" },
  { value: "02", label: "Febrero" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Mayo" },
  { value: "06", label: "Junio" },
  { value: "07", label: "Julio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "draft", label: "Borrador" },
  { value: "sent", label: "Enviado para facturar" },
  { value: "invoiced", label: "Facturado" },
  { value: "cancelled", label: "Cerrada" },
];

function normalizeBillingStatus(status) {
  switch (String(status || "").toLowerCase()) {
    case "requested":
      return "sent";
    case "issued":
      return "invoiced";
    case "closed":
      return "cancelled";
    case "draft":
    case "sent":
    case "invoiced":
    case "cancelled":
      return String(status).toLowerCase();
    default:
      return "draft";
  }
}

function billingStatusLabel(status) {
  switch (normalizeBillingStatus(status)) {
    case "sent":
      return "Enviado para facturar";
    case "invoiced":
      return "Facturado";
    case "cancelled":
      return "Cerrada";
    case "draft":
    default:
      return "Borrador";
  }
}

function statusBadgeClass(status) {
  switch (normalizeBillingStatus(status)) {
    case "sent":
      return "border-amber-400/30 bg-amber-500/15 text-amber-100";
    case "invoiced":
      return "border-emerald-400/30 bg-emerald-500/15 text-emerald-100";
    case "cancelled":
      return "border-rose-400/30 bg-rose-500/15 text-rose-100";
    default:
      return "border-white/15 bg-white/5 text-white/80";
  }
}

function formatPoLabel(r) {
  if (r?.po_base_number !== null && r?.po_base_number !== undefined && r?.po_base_number !== "") {
    return `OC-${r.po_base_number}`;
  }
  return r?.po_id ? String(r.po_id).slice(0, 8) : "—";
}

function buildDetailUrl(osId, invoiceId) {
  const base = window.location.origin;
  return `${base}/facturacion?id=${osId}&invoice_id=${invoiceId}`;
}

function buildOfferNumber(offer) {
  return offer?.code || offer?.base_number || "—";
}

function buildOfferRoute(offer) {
  const offerId = offer?.id || "";
  return offerId
    ? `/offers?ready_to_invoice=1&open=${encodeURIComponent(offerId)}`
    : "/offers?ready_to_invoice=1";
}

export default function FacturacionGerenciaPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [clientSearch, setClientSearch] = useState("");
  const [ccOrOcFilter, setCcOrOcFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");

  const [invoiceRows, setInvoiceRows] = useState([]);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [dismissedPendingIds, setDismissedPendingIds] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(window.localStorage.getItem("facturacion.gerencia.dismissed.v1") || "[]");
    } catch {
      return [];
    }
  });
  const [dismissedPendingItems, setDismissedPendingItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("facturacion.gerencia.dismissed.items.v1");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  });

  async function loadMe() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    if (!user) return null;

    const { data: prof, error: pErr } = await supabase
      .from("profiles")
      .select("id, full_name, role")
      .eq("id", user.id)
      .single();

    if (pErr) throw pErr;
    return prof;
  }

  async function loadInvoices() {
    const { data, error } = await supabase
      .from("v_billing_invoices_list")
      .select(
        [
          "invoice_id",
          "po_id",
          "po_base_number",
          "requested_at",
          "invoice_number",
          "issued_at",
          "status",
          "note",
          "created_at",
          "items_count",
          "total_amount",
          "os_id",
          "client_name",
          "client_cuit",
          "client_number",
          "billing_email_1",
          "billing_email_2",
          "oc_number",
          "accept_reference",
          "oc_pdf_url",
        ].join(",")
      )
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async function loadPendingOffers() {
    const cutoffIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: remitoRows, error: remitoErr } = await supabase
      .from("remitos")
      .select("id, os_id, code, official_number, date_out, status, created_at, updated_at")
      .gte("created_at", cutoffIso)
      .order("created_at", { ascending: false });
    if (remitoErr) throw remitoErr;

    const remitos = Array.isArray(remitoRows) ? remitoRows : [];
    if (!remitos.length) return [];

    const osIds = [...new Set(remitos.map((row) => row?.os_id).filter(Boolean))];
    if (!osIds.length) return [];

    const { data: osRows, error: osErr } = await supabase
      .from("service_orders")
      .select("id, po_id, code, created_at, updated_at")
      .in("id", osIds);
    if (osErr) throw osErr;

    const serviceOrders = Array.isArray(osRows) ? osRows : [];
    const poIds = [...new Set(serviceOrders.map((row) => row?.po_id).filter(Boolean))];
    if (!poIds.length) return [];

    const { data: poRows, error: poErr } = await supabase
      .from("purchase_orders")
      .select("id,offer_id,code,base_number")
      .in("id", poIds);
    if (poErr) throw poErr;

    const purchaseOrders = Array.isArray(poRows) ? poRows : [];
    const offerIds = [...new Set(purchaseOrders.map((row) => row?.offer_id).filter(Boolean))];
    if (!offerIds.length) return [];

    const { data: offersRows, error: offerErr } = await supabase
      .from("offers")
      .select("id,base_number,code,status,updated_at,client:clients!offers_client_id_fkey(id,name)")
      .in("id", offerIds);
    if (offerErr) throw offerErr;

    const offersById = new Map();
    (Array.isArray(offersRows) ? offersRows : []).forEach((offer) => {
      if (offer?.id && !offersById.has(offer.id)) offersById.set(offer.id, offer);
    });

    const osById = new Map();
    serviceOrders.forEach((os) => {
      if (os?.id && !osById.has(os.id)) osById.set(os.id, os);
    });

    const poById = new Map();
    purchaseOrders.forEach((po) => {
      if (po?.id && !poById.has(po.id)) poById.set(po.id, po);
    });

    const remitosByOsId = new Map();
    remitos.forEach((remito) => {
      const osId = remito?.os_id;
      if (!osId) return;
      const current = remitosByOsId.get(osId);
      const currentDate = current?.date_out || current?.created_at || current?.updated_at || "";
      const nextDate = remito?.date_out || remito?.created_at || remito?.updated_at || "";
      if (!current || String(nextDate) >= String(currentDate)) {
        remitosByOsId.set(osId, remito);
      }
    });

    return remitos
      .map((remito) => {
        const os = remito?.os_id ? osById.get(remito.os_id) || null : null;
        const po = os?.po_id ? poById.get(os.po_id) || null : null;
        const offer = po?.offer_id ? offersById.get(po.offer_id) || null : null;
        const latestRemito = os?.id ? remitosByOsId.get(os.id) || null : null;
        if (!os || !po || !offer || !latestRemito) return null;
        return { offer, os, po, remito: latestRemito };
      })
      .filter(Boolean)
      .filter((item) => !dismissedPendingIds.includes(item.offer?.id));
  }

  const refresh = async () => {
    setLoading(true);
    try {
      setError(null);
      const prof = await loadMe();
      setMe(prof);

      if (!prof) {
        setError("Necesitás iniciar sesión para ver esta página.");
        setInvoiceRows([]);
        setPendingOffers([]);
        return;
      }

      if (!["lab_manager", "admin"].includes(prof.role)) {
        setError("No tenés permisos para ver la bandeja de gerencia.");
        setInvoiceRows([]);
        setPendingOffers([]);
        return;
      }

      const [invoices, pending] = await Promise.all([loadInvoices(), loadPendingOffers()]);
      setInvoiceRows(invoices);
      setPendingOffers(pending);
    } catch (e) {
      console.error("[FacturacionGerenciaPage]", e);
      setError(e?.message || "No se pudo cargar la bandeja de gerencia.");
      setInvoiceRows([]);
      setPendingOffers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        "facturacion.gerencia.dismissed.v1",
        JSON.stringify(dismissedPendingIds)
      );
    } catch {
      // no-op
    }
  }, [dismissedPendingIds]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        "facturacion.gerencia.dismissed.items.v1",
        JSON.stringify(dismissedPendingItems)
      );
    } catch {
      // no-op
    }
  }, [dismissedPendingItems]);

  useEffect(() => {
    if (!me?.role) return undefined;

    const channel = supabase
      .channel("facturacion-gerencia-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "billing_invoices" }, () => {
        refresh();
      })
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "billing_invoice_items" },
        () => {
          refresh();
        }
      )
      .on("postgres_changes", { event: "*", schema: "public", table: "remitos" }, () => {
        refresh();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.role]);

  const filteredInvoices = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const ccQ = ccOrOcFilter.trim().toLowerCase();
    const monthQ = monthFilter.trim();

    return (invoiceRows || [])
      .filter((r) =>
        statusFilter === "all" ? true : normalizeBillingStatus(r.status) === statusFilter
      )
      .filter((r) => {
        if (!q) return true;
        const blob = [r.client_name, r.client_cuit, r.client_number].filter(Boolean).join(" | ").toLowerCase();
        return blob.includes(q);
      })
      .filter((r) => {
        if (!ccQ) return true;
        const poLabel = formatPoLabel(r);
        const ccLike = [r.oc_number, r.accept_reference, r.po_base_number, poLabel]
          .filter(Boolean)
          .join(" | ")
          .toLowerCase();
        return ccLike.includes(ccQ);
      })
      .filter((r) => {
        if (!monthQ) return true;
        const dates = [r.requested_at, r.issued_at, r.created_at]
          .filter(Boolean)
          .map((d) => {
            const iso = fmtDateInput(d);
            return iso ? iso.slice(5, 7) : "";
          });
        return dates.includes(monthQ);
      });
  }, [invoiceRows, statusFilter, clientSearch, ccOrOcFilter, monthFilter]);

  const countsByStatus = useMemo(() => {
    const map = new Map();
    for (const r of invoiceRows || []) {
      const st = normalizeBillingStatus(r.status);
      map.set(st, (map.get(st) || 0) + 1);
    }
    return map;
  }, [invoiceRows]);

  const totalAmount = useMemo(
    () => (filteredInvoices || []).reduce((acc, row) => acc + Number(row.total_amount || 0), 0),
    [filteredInvoices]
  );

  const dismissPending = (offerId) => {
    if (!offerId) return;
    const item = pendingOffers.find((row) => row.offer?.id === offerId) || null;
    setDismissedPendingIds((prev) => (prev.includes(offerId) ? prev : [...prev, offerId]));
    setDismissedPendingItems((prev) => {
      const next = prev.filter((x) => x.offerId !== offerId);
      return [
        ...next,
        {
          offerId,
          offerCode: item ? buildOfferNumber(item.offer) : offerId,
          clientName: item?.offer?.client?.name || "-",
          osCode: item?.os?.code || "-",
          remitoLabel: item?.remito?.official_number || item?.remito?.code || "-",
          dateOut: item?.remito?.date_out || "",
        },
      ];
    });
    setPendingOffers((prev) => prev.filter((item) => item.offer?.id !== offerId));
  };

  const restorePending = (offerId) => {
    if (!offerId) return;
    setDismissedPendingIds((prev) => prev.filter((id) => id !== offerId));
    setDismissedPendingItems((prev) => prev.filter((item) => item.offerId !== offerId));
    refresh();
  };

  const restoreAllPending = () => {
    setDismissedPendingIds([]);
    setDismissedPendingItems([]);
    refresh();
  };

  const openInvoice = (row) => {
    if (!row?.os_id || !row?.invoice_id) {
      alert("Esta factura no tiene OS asociada.");
      return;
    }
    window.open(buildDetailUrl(row.os_id, row.invoice_id), "_blank");
  };

  const copyInvoiceLink = async (row) => {
    if (!row?.os_id || !row?.invoice_id) {
      alert("Esta factura no tiene OS asociada.");
      return;
    }
    const url = buildDetailUrl(row.os_id, row.invoice_id);
    try {
      await navigator.clipboard.writeText(url);
      alert("Link copiado");
    } catch {
      prompt("Copiá el link:", url);
    }
  };

  const goToOffers = () => {
    navigate("/offers");
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white px-3 py-4 sm:px-6 lg:px-8">
      <div className="max-w-[1900px] mx-auto space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-xl font-semibold">Bandeja de facturación - Gerencia</div>
            <div className="text-xs text-white/60">
              Sesi&oacute;n: {me?.full_name || "—"} ({me?.role || "—"})
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-sm"
              disabled={loading}
            >
              {loading ? "Cargando..." : "Refrescar"}
            </button>
            <button
              onClick={goToOffers}
              className="px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-sm"
            >
              Ir a ofertas
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-900/20 p-3 text-sm text-amber-100">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">Facturas visibles</div>
            <div className="text-2xl font-semibold mt-1">{filteredInvoices.length}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">Enviado para facturar</div>
            <div className="text-2xl font-semibold mt-1">{countsByStatus.get("sent") || 0}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">Pendientes de revisión</div>
            <div className="text-2xl font-semibold mt-1">{pendingOffers.length}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">Total filtrado</div>
            <div className="text-2xl font-semibold mt-1">{fmtMoney(totalAmount)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
          <div className="xl:col-span-4 rounded-xl border border-amber-400/20 bg-amber-500/10 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">Pendientes de revisión</div>
                <div className="text-[11px] text-white/60">
                  Ofertas recientes listas para que gerencia revise antes de moverlas.
                </div>
              </div>
              <button
                onClick={() => setPendingOffers([])}
                className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10"
              >
                Limpiar vista
              </button>
            </div>

            <div className="mt-3 space-y-3">
              {pendingOffers.length === 0 ? (
                <div className="rounded-lg border border-white/10 bg-black/20 p-4 text-sm text-white/70">
                  No hay ofertas recientes para revisar.
                </div>
              ) : (
                pendingOffers.map((item) => {
                  const remitoLabel = item.remito
                    ? item.remito.official_number || item.remito.code || "-"
                    : "-";
                  return (
                    <div
                      key={item.offer.id}
                      className="rounded-xl border border-white/10 bg-black/20 p-4"
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          Oferta {buildOfferNumber(item.offer)}
                          <span className="rounded-full border border-amber-400/40 bg-amber-500/20 px-2 py-0.5 text-[11px] text-amber-50">
                            Lista para facturar
                          </span>
                        </div>
                        <div className="text-sm text-white/80">
                          {item.offer.client?.name || "-"}
                        </div>
                        <div className="text-[11px] text-white/60 flex flex-wrap gap-x-3 gap-y-1">
                          <span>OS: {item.os?.code || "-"}</span>
                          <span>Remito: {remitoLabel}</span>
                          <span>Fecha: {fmtDate(item.remito?.date_out)}</span>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm hover:bg-white/15"
                          onClick={() => window.open(buildOfferRoute(item.offer), "_blank")}
                        >
                          Ver ofertas
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 hover:bg-amber-500/15"
                          onClick={() => window.open(buildOfferRoute(item.offer), "_blank")}
                        >
                          Ir a facturar
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-emerald-500/30 bg-emerald-500/15 px-3 py-2 text-sm text-emerald-50 hover:bg-emerald-500/25"
                          onClick={() => dismissPending(item.offer.id)}
                        >
                          Marcar revisada
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold">Revisadas ocultas</div>
                  <div className="text-[11px] text-white/60">
                    Ofertas que se ocultaron por error o que el gerente quiere volver a ver.
                  </div>
                </div>
                <button
                  onClick={restoreAllPending}
                  className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10"
                  disabled={dismissedPendingItems.length === 0}
                >
                  Restaurar todas
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {dismissedPendingItems.length === 0 ? (
                  <div className="text-xs text-white/60">No hay ofertas ocultas.</div>
                ) : (
                  dismissedPendingItems.map((item) => (
                    <div
                      key={item.offerId}
                      className="rounded-lg border border-white/10 bg-white/5 p-3 flex flex-col gap-2"
                    >
                      <div className="text-sm font-semibold">
                        Oferta {item.offerCode}
                      </div>
                      <div className="text-[11px] text-white/60">
                        {item.clientName} · OS: {item.osCode} · Remito: {item.remitoLabel}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => restorePending(item.offerId)}
                          className="px-2 py-1 rounded bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-xs"
                        >
                          Restaurar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="xl:col-span-8 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-col lg:flex-row lg:items-end gap-3">
              <div className="flex-1">
                <div className="text-xs text-white/60 mb-1">Buscar por cliente</div>
                <input
                  className="w-full rounded bg-black/40 border border-white/15 p-3 text-sm"
                  placeholder="Cliente, CUIT o número"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <div className="text-xs text-white/60 mb-1">Buscar por CC u OC</div>
                <input
                  className="w-full rounded bg-black/40 border border-white/15 p-3 text-sm"
                  placeholder="Centro de costo, OC o referencia"
                  value={ccOrOcFilter}
                  onChange={(e) => setCcOrOcFilter(e.target.value)}
                />
              </div>
              <div className="w-full lg:w-52">
                <div className="text-xs text-white/60 mb-1">Buscar por mes</div>
                <select
                  className="w-full rounded bg-black/40 border border-white/15 p-3 text-sm"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                >
                  {MONTH_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    statusFilter === opt.value
                      ? "bg-amber-500/20 border-amber-400/30 text-amber-100"
                      : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                  }`}
                >
                  {opt.label}
                  {opt.value !== "all" ? ` (${countsByStatus.get(opt.value) || 0})` : ""}
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-3">
              {filteredInvoices.length === 0 ? (
                <div className="rounded-lg border border-white/10 bg-black/20 p-4 text-sm text-white/70">
                  No hay facturas para mostrar.
                </div>
              ) : (
                filteredInvoices.map((row) => {
                  const poLabel = formatPoLabel(row);
                  const ocRef = row.oc_number || row.accept_reference || null;
                  const emails = [row.billing_email_1, row.billing_email_2].filter(Boolean).join(", ");
                  return (
                    <div
                      key={row.invoice_id}
                      className="rounded-xl border border-white/10 bg-black/20 p-4"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-white">
                              {row.client_name || "—"}
                            </div>
                            <span
                              className={`text-[11px] px-2 py-1 rounded-full border ${statusBadgeClass(row.status)}`}
                            >
                              {billingStatusLabel(row.status)}
                            </span>
                          </div>
                          <div className="text-xs text-white/60">
                            {row.client_cuit ? `CUIT: ${row.client_cuit}` : "—"}
                            {row.client_number ? ` · Cliente Nº: ${row.client_number}` : ""}
                          </div>
                          {emails ? <div className="text-xs text-white/50">{emails}</div> : null}
                          <div className="text-xs text-white/60 flex flex-wrap gap-x-3 gap-y-1">
                            <span>{poLabel}</span>
                            {ocRef ? <span>{ocRef}</span> : null}
                            <span>Pedido: {fmtDate(row.requested_at)}</span>
                            <span>Emisión: {fmtDate(row.issued_at)}</span>
                          </div>
                          <div className="text-[11px] text-white/45">
                            {row.note || "Sin nota"}
                          </div>
                        </div>

                        <div className="flex flex-col items-start lg:items-end gap-2 shrink-0">
                          <div className="text-xs text-white/60">
                            Ítems: <b className="text-white">{row.items_count ?? 0}</b>
                          </div>
                          <div className="text-lg font-semibold text-white">
                            {fmtMoney(row.total_amount)}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => openInvoice(row)}
                              className="px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-sm"
                            >
                              Abrir detalle
                            </button>
                            <button
                              onClick={() => copyInvoiceLink(row)}
                              className="px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-sm"
                            >
                              Copiar link
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="text-xs text-white/60">
          En esta bandeja la etiqueta <b>Enviado para facturar</b> es informativa. La edición operativa queda en el laboratorio.
        </div>
      </div>
    </div>
  );
}
